import { forwardRef, type TextareaHTMLAttributes } from "react";

import { cn } from "./lib/cn.js";

/**
 * TextArea primitive.
 *
 * Canon: docs/design-canon/01-ui/forms.md R2 +
 *        docs/design-canon/01-ui/input-primitives.md.
 * Same uniform shape as <Input> / <Select>: auto-wires `htmlFor`/`id`,
 * surfaces label + error, `min-w-0 w-full` so it works inside flex
 * parents.
 */
export interface TextAreaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
}

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  ({ className, label, error, id, "aria-describedby": describedBy, ...props }, ref) => {
    // Announce the error to assistive tech (assessment U6).
    const errorId = id ? `${id}-error` : undefined;
    const describedByIds = [describedBy, error && errorId ? errorId : undefined]
      .filter(Boolean)
      .join(" ");
    return (
      <div className="space-y-1">
        {label && (
          <label htmlFor={id} className="block text-sm font-medium text-fg">
            {label}
          </label>
        )}
        <textarea
          ref={ref}
          id={id}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedByIds || undefined}
          className={cn(
            // min-w-0 mandatory inside flex parents (canon 01-ui/layout.md R1).
            "block w-full min-w-0 rounded-soft-md border px-3 py-2 text-sm shadow-sm transition-colors",
            "placeholder:text-fg-subtle",
            "focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand",
            "disabled:bg-surface-muted disabled:text-fg-subtle",
            error
              ? "border-danger-border-strong focus:border-danger-solid focus:ring-danger-solid"
              : "border-strong",
            className,
          )}
          {...props}
        />
        {error && (
          <p id={errorId} role="alert" className="text-xs text-danger-solid">
            {error}
          </p>
        )}
      </div>
    );
  },
);
TextArea.displayName = "TextArea";
